import {
  type ExamLocationRequestApprovalType,
  ExamLocationRequestWorkflowStepCode,
  type ExamLocationVerdict,
} from '@exam-center/graphql';
import { Divider, Flex, PaperSection, Text } from '@shared/components';
import { dayjs, formatDateTime } from '@shared/utils';
import { Fragment } from 'react';

import { ExamCenterLocationRequestVerdictResultCard } from '../../components';

export interface ExamCenterLocationRequestVerdictFragment {
  id: string;
  requestedAt: Date;
  submittedAt: Date | null;
  updatedUser: {
    firstname: string;
    lastname: string;
  } | null;
  comment: string | null;
  verdict: ExamLocationVerdict;
  step: ExamLocationRequestWorkflowStepCode;
  approvalType: ExamLocationRequestApprovalType | null;
}

interface ExamCenterLocationRequestVerdictHistoryProps {
  verdicts: ExamCenterLocationRequestVerdictFragment[];
}

function examCenterLocationRequestVerdictData(
  examCenterLocationRequestVerdict: ExamCenterLocationRequestVerdictFragment,
) {
  const fullName = [
    examCenterLocationRequestVerdict.updatedUser?.firstname,
    examCenterLocationRequestVerdict.updatedUser?.lastname,
  ]
    .filter(Boolean)
    .join(' ');
  const comment =
    examCenterLocationRequestVerdict.comment && examCenterLocationRequestVerdict.comment.length > 0
      ? examCenterLocationRequestVerdict.comment
      : '-';
  const submittedAt = formatDateTime(examCenterLocationRequestVerdict.submittedAt);
  const approverNumber =
    examCenterLocationRequestVerdict.step ===
    ExamLocationRequestWorkflowStepCode.ExamLocationRequestInReview_1
      ? 1
      : 2;
  const verdict = examCenterLocationRequestVerdict.verdict;
  return {
    fullName,
    comment,
    submittedAt,
    approverNumber,
    verdict,
    approvalType: examCenterLocationRequestVerdict.approvalType,
  };
}

export default function ExamCenterLocationRequestVerdictHistory(
  props: Readonly<ExamCenterLocationRequestVerdictHistoryProps>,
) {
  const { verdicts } = props;

  const grouped = verdicts.reduce<Record<string, ExamCenterLocationRequestVerdictFragment[]>>(
    (acc, verdict) => {
      const requestKey = verdict.requestedAt.toISOString();
      const isSubmitted = verdict.submittedAt !== null;
      if (isSubmitted) {
        if (!acc[requestKey]) {
          acc[requestKey] = [];
        }
        acc[requestKey].push(verdict);
      }
      return acc;
    },
    {},
  );

  const orderedKeys = Object.keys(grouped).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const ordered: Record<string, ExamCenterLocationRequestVerdictFragment[]> = {};
  for (const k of orderedKeys) {
    ordered[k] = grouped[k].sort(
      (a, b) => dayjs(a.submittedAt).valueOf() - dayjs(b.submittedAt).valueOf(),
    );
  }
  return (
    <PaperSection title='ผลการอนุมัติ'>
      <Flex
        sx={{
          p: (theme) => theme.spacing(6),
          height: '100%',
          gap: (theme) => theme.spacing(6),
          flexDirection: 'column',
        }}
      >
        {Object.entries(ordered).map(([key, value], idx, arr) => (
          <Fragment key={key}>
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <Text variant='h5'>วันที่ส่งคำขอ: {formatDateTime(new Date(key))} </Text>
              {value.map((verdict) => {
                const {
                  fullName,
                  comment,
                  submittedAt,
                  approverNumber,
                  verdict: verdictValue,
                  approvalType,
                } = examCenterLocationRequestVerdictData(verdict);
                return (
                  <ExamCenterLocationRequestVerdictResultCard
                    key={verdict.id}
                    fullName={fullName}
                    comment={comment}
                    submittedAt={submittedAt}
                    approverNumber={approverNumber}
                    verdict={verdictValue}
                    approvalType={approvalType}
                  />
                );
              })}
            </Flex>
            {idx < arr?.length - 1 ? <Divider /> : null}
          </Fragment>
        ))}
      </Flex>
    </PaperSection>
  );
}
